<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { usePermissions } from '../usePermissions.ts';
import InfoMessage from '@/components/InfoMessage/InfoMessage.vue';
import { I18nKey } from '../../../utils/typings.ts';
import { SelectPermissionsProps } from '@/views/roles/types.ts';
import { AppPermissionsEnum } from '@/api/generator';

const modelValue = defineModel<AppPermissionsEnum[]>({ required: true });
const props = defineProps<SelectPermissionsProps>();

const lockedPermissions = ref<string[]>(props.lockedPermissions);

const firstColumnLevel = 2;
const {
  initPermissions,
  getGroupGrantedCount,
  toggleAllPermissionsOfGroup,
  grantPermission,
  getTranslationKeyByPermissionId,
  devideAndCategorizePermissionGroups,
  categoryIdToGroupIdsMap,
  groupedPermissions,
  initialzing,
  permissionItems,
} = usePermissions(modelValue.value, lockedPermissions, firstColumnLevel);

const panelsOfCategory = ref<Record<string, string[]>>({});

watch(
  () => props.lockedPermissions,
  () => {
    lockedPermissions.value = props.lockedPermissions;
  },
);

watch(
  () => permissionItems.value,
  (_, oldVal) => {
    if (!initialzing.value && permissionItems.value && oldVal)
      modelValue.value = permissionItems.value
        .filter((item) => item.granted)
        .map((item) => item.id!);
  },
  {
    deep: true,
  },
);

onMounted(() => {
  initPermissions(props.allPermissions);
  Object.keys(categoryIdToGroupIdsMap.value).forEach((key) => {
    panelsOfCategory.value[key] = [];
  });
});

function clearAllPermissionsOfCategory(category: string) {
  categoryIdToGroupIdsMap.value[category].forEach((groupName) => {
    toggleAllPermissionsOfGroup(groupedPermissions.value[groupName]);
  });
}
</script>

<template>
  <div class="expansion">
    <v-sheet v-for="category in Object.keys(categoryIdToGroupIdsMap)" :key="category" class="mb-4">
      <div class="d-flex justify-space-between align-center">
        <span class="text-h6">
          {{ $t(category as I18nKey) }}
        </span>
        <span class="d-flex align-center">
          <TextVBtn @click="panelsOfCategory[category] = categoryIdToGroupIdsMap[category]">{{
            $t('ExpandAllPermissionPanels')
          }}</TextVBtn>
          <TextVBtn @click="panelsOfCategory[category] = []">{{
            $t('CollapseAllPermissionPanels')
          }}</TextVBtn>
          <TextVBtn @click="clearAllPermissionsOfCategory(category)">{{
            $t('ClearAllPermissions')
          }}</TextVBtn>
        </span>
      </div>
      <v-expansion-panels v-model="panelsOfCategory[category]" class="expansion" :multiple="true">
        <v-row>
          <v-col
            v-for="(splittedDict, splittedIndex) in devideAndCategorizePermissionGroups(
              category,
              categoryIdToGroupIdsMap,
              groupedPermissions,
              3,
            )"
            :key="splittedIndex"
            cols="12"
            lg="4"
          >
            <v-expansion-panel
              v-for="(group, index) in splittedDict"
              :key="group[0]?.id"
              class="ma-4"
              :value="index"
              elevation="2"
            >
              <v-expansion-panel-title>
                <span class="d-flex align-center">
                  <span class="text-h4 text-primary-darken-2">{{
                    $t(getTranslationKeyByPermissionId(index.toString()) as I18nKey)
                  }}</span>
                  <span class="ml-6 text-body-2 font-weight-medium text-primary"
                    >{{ getGroupGrantedCount(group) }} of {{ group.length }}
                    {{ $t('PermissionsAssigned') }}
                  </span>
                </span>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-divider thickness="2"></v-divider>
                <span class="d-flex justify-end mt-4">
                  <GhostVBtn
                    class="text-caption"
                    color="secondary"
                    size="x-small"
                    @click="toggleAllPermissionsOfGroup(group)"
                    >{{
                      getGroupGrantedCount(group) < group.length
                        ? $t('SelectAllPermissions')
                        : $t('DeselectAllPermissions')
                    }}</GhostVBtn
                  >
                </span>

                <v-list
                  :items="
                    group.map((permissionGroup) => ({
                      title: permissionGroup.id,
                      value: permissionGroup,
                    }))
                  "
                  density="compact"
                  itemTitle="name"
                  itemValue="value"
                >
                  <template #item="{ props: slotProps }">
                    <div
                      :style="`margin-left: ${
                        32 * (slotProps.value.level - 2 >= 0 ? slotProps.value.level - 2 : 0)
                      }px;`"
                    >
                      <span class="d-flex"
                        ><v-checkbox-btn
                          v-model="slotProps.value.granted"
                          :class="{ locked: slotProps.value.isLocked }"
                          :disabled="
                            slotProps.value.disabled ||
                            slotProps.value.isLocked ||
                            slotProps.value.hasLockedChilds
                          "
                          @change="grantPermission(slotProps.value, slotProps.value.granted)"
                        >
                          <template #label>
                            {{ $t(slotProps.value.i18n) }}
                          </template>
                        </v-checkbox-btn>
                        <InfoMessage
                          v-if="slotProps.value.isLocked"
                          :value="$t('LockedToPreventSelfLockoutMsg')"
                          size="12"
                        ></InfoMessage
                      ></span>
                    </div>
                  </template>
                </v-list>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-col>
        </v-row>
      </v-expansion-panels>
      <v-divider thickness="2"></v-divider>
    </v-sheet>
  </div>
</template>

<style scoped lang="scss">
.expansion {
  z-index: unset;
}

::v-deep(.v-expansion-panel-title__icon) {
  color: rgb(var(--v-theme-secondary));
}

::v-deep(.v-expansion-panel-title__overlay) {
  background-color: unset;
}

.locked {
  flex: unset;
}
</style>
